import React, { useEffect, useState } from 'react';
import '../App.css';
import '../enquiry-ui.css';
import Icon from '../components/Icon';
import MasterCard from '../components/MasterCard';
import ListingPagination from '../components/ListingPagination';
import { fetchDashboard } from '../services/dashboardApi';
import {
  bulkUpdateStatus, deleteEnquiry, exportEnquiries, fetchBin, fetchEnquiries,
  fetchFilterOptions, fetchImportTemplate, importEnquiries, permanentlyDeleteFromBin,
  restoreFromBin, updateEnquiryStatus,
} from '../services/enquiriesApi';
import { useEnquiryCounts } from '../context/EnquiryContext';
import { createLocation, deleteLocation, fetchLocations, updateLocation } from '../services/locationApi';
import { createMailId, deleteMailId, fetchMailIds, resendPendingNotifications, updateMailId } from '../services/mailIdsApi';
import { State, City } from 'country-state-city';

const INDIA_STATES = State.getStatesOfCountry('IN');
const INDIA_STATE_NAMES = INDIA_STATES.map(s => s.name);
const builtInCityNames = stateName => {
  const state = INDIA_STATES.find(s => s.name.toLowerCase() === stateName.trim().toLowerCase());
  return state ? City.getCitiesOfState('IN', state.isoCode).map(c => c.name) : [];
};

const sectionNames = {
  'distributor-enquiries': 'Distributor Enquiries', 'super-stockist-enquiries': 'Super Stockist Enquiries',
  'opening-bin': 'Opening Bin', 'application-bin': 'Application Bin',
};

const ENQUIRY_STATUSES = ['New', 'In Progress', 'Contacted', 'Closed'];
const STATUS_STYLE = {
  New: { color: '#c67f2f', background: '#fff1e6' },
  'In Progress': { color: '#3982c9', background: '#e9f4ff' },
  Contacted: { color: '#5654d7', background: '#eeeeff' },
  Closed: { color: '#29956c', background: '#e9f8f1' },
};
const DEFAULT_PAGE_SIZE = 10;

const fullName = e => [e.first_name, e.middle_name, e.last_name].filter(Boolean).join(' ') || '-';
const stateField = type => (type === 'distributors' ? 'agency_state' : 'current_state');
const cityField = type => (type === 'distributors' ? 'agency_city' : 'current_city');
const enquiryState = (e, type) => e[stateField(type)] || '-';
const enquiryCity = (e, type) => e[cityField(type)] || '-';
const formatDate = iso => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-');

const FIELD_LABELS = {
  agency_state: 'Agency State', agency_city: 'Agency City',
  first_name: 'First Name', middle_name: 'Middle Name', last_name: 'Last Name',
  dob: 'Date of Birth', mobile: 'Mobile Number', email: 'Email',
  educational_details: 'Educational Details',
  current_address: 'Current Address', current_state: 'Current State', current_city: 'Current City', current_pincode: 'Current Pincode',
  experience: 'Experience', designation: 'Designation', work_profile: 'Work Profile',
  company_name: 'Company Name', company_email: 'Company Email',
  company_address: 'Company Address', company_state: 'Company State', company_city: 'Company City', company_pincode: 'Company Pincode',
  firm_type: 'Firm Type', yearly_turnover: 'Yearly Turnover', year_of_establishment: 'Year of Establishment',
  partner_first_name: 'Partner First Name', partner_middle_name: 'Partner Middle Name', partner_last_name: 'Partner Last Name',
  partner_dob: 'Partner Date of Birth', partner_mobile: 'Partner Mobile Number', partner_email: 'Partner Email',
  partner_address: 'Partner Address', partner_state: 'Partner State', partner_city: 'Partner City',
  product_details: 'Product Details',
  storage_facility_availability: 'Storage Facility Availability', godown_size: 'Godown Size',
  transport_facility_availability: 'Transport Facility Availability', vehicle_model: 'Vehicle Model', no_of_vehicles: 'No. of Vehicles',
  investment_capacity: 'Investment Capacity', working_capital: 'Working Capital',
  current_business: 'Current Business', size_of_business: 'Size of Business', primary_contact_person: 'Primary Contact Person',
  business_details: 'Business Details', current_investment: 'Current Investment', infrastructure_details: 'Infrastructure Details',
  createdAt: 'Received On', updatedAt: 'Last Updated',
};

const fieldLabel = k => FIELD_LABELS[k] || k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

function PageHeader({ eyebrow, name, description, action }) {
  return <div className="page-heading"><div><span>{eyebrow}</span><h1>{name}</h1><p>{description}</p></div>{action}</div>;
}

function StatusBadge({ status }) {
  const style = STATUS_STYLE[status] || {};
  return <span className="status" style={style}><i className="status-dot" style={{ background: style.color }} /> {status}</span>;
}

const selectStyle = { minHeight: 36, border: '1px solid #dfe1e9', borderRadius: 7, padding: '0 8px', background: '#fff', color: '#313444', fontSize: 12, fontWeight: 600 };

function triggerDownload(result) {
  const mime = result.filename.endsWith('.csv')
    ? 'text/csv'
    : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const bytes = Uint8Array.from(atob(result.data), c => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
  const a = document.createElement('a');
  a.href = url; a.download = result.filename; a.click(); URL.revokeObjectURL(url);
}

/* ------------------------------- Overview ------------------------------- */

function TrendChart({ trend }) {
  const totals = trend.map(p => p.distributor + p.superStockist);
  const max = Math.max(1, ...totals);
  const niceMax = Math.max(4, Math.ceil(max / 4) * 4);
  const n = trend.length;
  const x = i => (n > 1 ? (i / (n - 1)) * 700 : 0);
  const y = v => 210 - (v / niceMax) * 210;
  const line = totals.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const area = `${line} L 700,210 L 0,210 Z`;
  const yLabels = [niceMax, Math.round((niceMax * 2) / 3), Math.round(niceMax / 3), 0];
  return (
    <section className="panel analytics-panel">
      <div className="panel-title"><div><h2>Enquiry Analytics</h2><p>Total enquiries received over the last {n} days</p></div></div>
      <div className="chart-area">
        <div className="y-labels">{yLabels.map((l, i) => <span key={i}>{l}</span>)}</div>
        <div className="chart-plot">
          <div className="grid-line one" /><div className="grid-line two" /><div className="grid-line three" />
          <svg viewBox="0 0 700 210" preserveAspectRatio="none">
            <defs><linearGradient id="fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#5b5bd6" stopOpacity=".22" /><stop offset="1" stopColor="#5b5bd6" stopOpacity="0" /></linearGradient></defs>
            <path className="area" d={area} />
            <path className="line" d={line} />
          </svg>
          <div className="months">{trend.filter((_, i) => i % 2 === 0).map(p => <span key={p.date}>{new Date(`${p.date}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>)}</div>
        </div>
      </div>
    </section>
  );
}

function Overview() {
  const [dashboard, setDashboard] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchDashboard().catch(() => null),
      fetchEnquiries('distributors', { limit: 6, sortBy: 'createdAt', sortDir: 'desc' }).catch(() => ({ data: [] })),
      fetchEnquiries('super-stockists', { limit: 6, sortBy: 'createdAt', sortDir: 'desc' }).catch(() => ({ data: [] })),
    ]).then(([dash, d, s]) => {
      if (cancelled) return;
      setDashboard(dash);
      const merged = [
        ...(d.data || []).map(e => ({ ...e, _type: 'distributor' })),
        ...(s.data || []).map(e => ({ ...e, _type: 'super-stockist' })),
      ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 6);
      setRecent(merged);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <p className="api-message">Loading dashboard...</p>;
  const s = dashboard?.stats;
  const stats = [
    ['Total Distributor', s?.totalDistributor ?? 0, 'All-time distributor enquiries', 'purple', 'users'],
    ['Total Super Stockist', s?.totalSuperStockist ?? 0, 'All-time super stockist enquiries', 'blue', 'users'],
    ["Today's Enquiries", s?.today ?? 0, 'Received today', 'orange', 'clock'],
    ['This Month', s?.thisMonth ?? 0, 'Received this month', 'green', 'trend'],
    ['New Enquiries', s?.newEnquiries ?? 0, 'Enquiries currently marked as New', 'orange', 'plus'],
    ['Closed Enquiries', s?.closed ?? 0, 'Enquiries currently marked as Closed', 'green', 'check'],
  ];
  return <>
    <PageHeader eyebrow="CHANNEL PARTNER OVERVIEW" name="Dashboard" description="Live counts across the Distributor and Super Stockist enquiry forms." />
    <div className="stat-grid">{stats.map(([label, value, note, color, icon]) => (
      <article className="stat-card" key={label}><div className={`stat-icon ${color}`}><Icon name={icon} /></div><div className="stat-copy"><span>{label}</span><strong>{value}</strong><small>{note}</small></div></article>
    ))}</div>
    {/* {dashboard?.trend && <TrendChart trend={dashboard.trend} />}
    <section className="panel jobs-panel" style={{ marginTop: 18 }}>
      <div className="panel-title"><div><h2>Recent Enquiries</h2><p>Latest submissions across both forms</p></div></div>
      <div className="table-wrap"><table><thead><tr><th>NAME</th><th>TYPE</th><th>MOBILE</th><th>LOCATION</th><th>STATUS</th><th>RECEIVED</th></tr></thead>
        <tbody>{recent.length === 0
          ? <tr><td colSpan={6} style={{ color: '#9a9daa' }}>No enquiries yet.</td></tr>
          : recent.map(e => <tr key={`${e._type}-${e._id}`}>
            <td><strong>{fullName(e)}</strong></td>
            <td>{e._type === 'distributors' ? 'Distributors' : 'Super Stockists'}</td>
            <td>{e.mobile || '-'}</td>
            <td><span className="icon-text"><Icon name="pin" /> {enquiryCity(e, e._type)}, {enquiryState(e, e._type)}</span></td>
            <td><StatusBadge status={e.status || 'New'} /></td>
            <td>{formatDate(e.createdAt)}</td>
          </tr>)}</tbody></table></div>
    </section> */}
  </>;
}

/* ---------------------------- Enquiry section --------------------------- */

const emptyFilters = { status: '', state: '', city: '', dateFrom: '', dateTo: '' };

function ImportModal({ type, title, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [rowErrors, setRowErrors] = useState([]);

  const downloadTemplate = async () => {
    setDownloading(true); setError('');
    try { triggerDownload(await fetchImportTemplate(type)); }
    catch (err) { setError(err.message); }
    finally { setDownloading(false); }
  };

  const upload = async e => {
    e.preventDefault();
    if (!file) { setError('Please choose a file'); return; }
    setUploading(true); setError(''); setRowErrors([]);
    try {
      const res = await importEnquiries(type, file);
      onDone(res.inserted || 0);
    } catch (err) { setError(err.message); setRowErrors(err.details || []); }
    finally { setUploading(false); }
  };

  return <div className="modal-backdrop" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
    <form className="import-modal" onSubmit={upload}>
      <h2>Import {title}</h2>
      <p>Upload an .xlsx or .csv file. Download the template to see the required columns. Max 5 MB / 2000 rows.</p>
      <button type="button" className="secondary-action bulk-template-button" onClick={downloadTemplate} disabled={downloading}><Icon name="download" /> {downloading ? 'Preparing...' : 'Download template'}</button>
      <label className={`bulk-drop-zone${file ? ' selected' : ''}`} htmlFor={`imp-${type}`} style={{ marginTop: 18 }}>
        <Icon name="file" /><strong>{file ? file.name : 'Choose a file'}</strong>
        <span>{file ? `${(file.size / 1024).toFixed(1)} KB selected` : 'Click to browse (.xlsx, .csv)'}</span>
        <input id={`imp-${type}`} type="file" accept=".xlsx,.xls,.csv" onChange={e => { setFile(e.target.files?.[0] || null); setError(''); setRowErrors([]); }} />
      </label>
      {error && <div className="bulk-upload-error"><strong>{error}</strong>{rowErrors.length > 0 && <ul>{rowErrors.slice(0, 20).map((it, i) => <li key={i}>Row {it.row}: {it.message}</li>)}</ul>}{rowErrors.length > 20 && <small>And {rowErrors.length - 20} more.</small>}</div>}
      <div className="confirm-actions"><button type="button" className="secondary-action" onClick={onClose}>Cancel</button><button type="submit" className="primary-action" disabled={!file || uploading}><Icon name="upload" /> {uploading ? 'Importing...' : 'Import'}</button></div>
    </form>
  </div>;
}

function EnquirySection({ type, title }) {
  const { setCounts } = useEnquiryCounts();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [filters, setFilters] = useState(emptyFilters);
  const [sort, setSort] = useState({ by: 'createdAt', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [reload, setReload] = useState(0);
  const [resp, setResp] = useState({ data: [], pagination: { page: 1, total: 0, totalPages: 1, limit: DEFAULT_PAGE_SIZE } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [options, setOptions] = useState({ states: [], cities: [], statuses: ENQUIRY_STATUSES });
  const [selected, setSelected] = useState(() => new Set());
  const [bulkStatus, setBulkStatus] = useState('New');
  const [details, setDetails] = useState(null);
  const [detailStatus, setDetailStatus] = useState('New');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [actionError, setActionError] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const refresh = () => setReload(n => n + 1);

  // debounce search (also reset to page 1)
  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { fetchFilterOptions(type).then(setOptions).catch(() => {}); }, [type]);

  // main data fetch
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { page, limit: pageSize, sortBy: sort.by, sortDir: sort.dir, q: debounced, ...filters };
    fetchEnquiries(type, params)
      .then(res => { if (!cancelled) { setResp({ data: res.data || [], pagination: res.pagination || {} }); if (!debounced && Object.values(filters).every(value => !value)) setCounts(current => ({ ...current, [type === 'distributors' ? 'distributor' : 'superStockist']: res.pagination?.total || 0 })); setError(''); setSelected(new Set()); } })
      .catch(err => { if (!cancelled) { setError(err.message); setResp({ data: [], pagination: { page: 1, total: 0, totalPages: 1 } }); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [type, page, pageSize, sort, debounced, filters, reload, setCounts]);

  const setFilter = (key, value) => { setFilters(f => ({ ...f, [key]: value })); setPage(1); };
  const clearAll = () => { setSearch(''); setDebounced(''); setFilters(emptyFilters); setSort({ by: 'createdAt', dir: 'desc' }); setPage(1); };
  const changeSort = field => { setSort(s => ({ by: field, dir: s.by === field && s.dir === 'desc' ? 'asc' : 'desc' })); setPage(1); };

  const { data, pagination } = resp;
  const toggleRow = id => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allChecked = data.length > 0 && data.every(e => selected.has(e._id));
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(data.map(e => e._id)));

  const rowStatusChange = async (e, status) => {
    setActionError('');
    try { await updateEnquiryStatus(type, e._id, status); refresh(); }
    catch (err) { setActionError(err.message); }
  };
  const applyBulk = async () => {
    setActionError(''); setBulkLoading(true);
    try { await bulkUpdateStatus(type, [...selected], bulkStatus); refresh(); }
    catch (err) { setActionError(err.message); }
    finally { setBulkLoading(false); }
  };
  const confirmDelete = async () => {
    setActionError(''); setBulkLoading(true);
    try {
      await Promise.all(pendingDelete.ids.map(id => deleteEnquiry(type, id)));
      const removed = pendingDelete.ids.length;
      const activeKey = type === 'distributors' ? 'distributor' : 'superStockist';
      const binKey = type === 'distributors' ? 'distributorBin' : 'superStockistBin';
      setCounts(current => ({ ...current, [activeKey]: Math.max(0, (current[activeKey] || 0) - removed), [binKey]: (current[binKey] || 0) + removed }));
      setPendingDelete(null); refresh();
    }
    catch (err) { setActionError(err.message); }
    finally { setBulkLoading(false); }
  };
  const runExport = async format => {
    setActionError('');
    const params = { q: debounced, ...filters, sortBy: sort.by, sortDir: sort.dir };
    try { triggerDownload(await exportEnquiries(type, params, format, [...selected])); }
    catch (err) { setActionError(err.message); }
  };
  const openDetails = e => { setDetails(e); setDetailStatus(e.status || 'New'); };
  const saveDetailStatus = async () => {
    try { const updated = await updateEnquiryStatus(type, details._id, detailStatus); setDetails(updated); refresh(); }
    catch (err) { setActionError(err.message); }
  };

  const headers = [
    ['NAME', 'first_name'], ['COMPANY', 'company_name'], ['MOBILE', 'mobile'],
    ['AGENCY STATE', stateField(type)], ['AGENCY CITY', cityField(type)], ['STATUS', 'status'], ['RECEIVED', 'createdAt'],
  ];
  return <>
    <PageHeader eyebrow="CHANNEL PARTNER" name={title} description={`${pagination.total ?? 0} ${title.toLowerCase()} in total.`}
      action={<span className="toolbar-actions">
        <button type="button" className="secondary-action" onClick={() => setShowImport(true)}><Icon name="upload" /> Import</button>
        <details className={`export-menu${!data.length ? ' disabled' : ''}`}>
          <summary className="secondary-action" aria-disabled={!data.length} onClick={event => { if (!data.length) event.preventDefault(); }} title={selected.size ? `Export ${selected.size} selected ${selected.size === 1 ? 'enquiry' : 'enquiries'}` : 'Export all matching enquiries'}><Icon name="download" /> Export{selected.size ? ` (${selected.size})` : ''} <span className="export-chevron" aria-hidden="true" /></summary>
          <div className="export-menu-options">
            <button type="button" disabled={!data.length} onClick={event => { event.currentTarget.closest('details').removeAttribute('open'); runExport('csv'); }}><span><strong>Export as CSV</strong></span></button>
            <button type="button" disabled={!data.length} onClick={event => { event.currentTarget.closest('details').removeAttribute('open'); runExport('xlsx'); }}><span><strong>Export as Excel</strong></span></button>
          </div>
        </details>
      </span>} />

    <section className="panel jobs-panel">
      <div className="filter-bar">
        <input className="filter-search" placeholder="Search name, company, email, mobile..." value={search} onChange={e => setSearch(e.target.value)} />
        <select value={filters.status} onChange={e => setFilter('status', e.target.value)}><option value="">All statuses</option>{ENQUIRY_STATUSES.map(s => <option key={s}>{s}</option>)}</select>
        <select value={filters.state} onChange={e => setFilter('state', e.target.value)}><option value="">All states</option>{options.states.map(s => <option key={s}>{s}</option>)}</select>
        <select value={filters.city} onChange={e => setFilter('city', e.target.value)}><option value="">All cities</option>{options.cities.map(c => <option key={c}>{c}</option>)}</select>
         </div>
         <div className="filter-bar">
           <label>From<input type="date" value={filters.dateFrom} onChange={e => setFilter('dateFrom', e.target.value)} /></label>
        <label>To<input type="date" value={filters.dateTo} onChange={e => setFilter('dateTo', e.target.value)} /></label>
        <button type="button" className="reset-filters" onClick={clearAll}><span aria-hidden="true">↻</span> Reset filters</button>
    
         </div>

      {selected.size > 0 && <div className="bulk-bar bin-bulk-bar active-bulk-bar">
        <div className="bulk-selection-count"><span>{selected.size}</span><div><strong>{selected.size === 1 ? 'Enquiry selected' : 'Enquiries selected'}</strong><small>Update the status or move selected records to the bin</small></div></div>
        <div className="bulk-actions">
          <label className="bulk-status-field"><span>Status</span><select value={bulkStatus} onChange={e => setBulkStatus(e.target.value)}>{ENQUIRY_STATUSES.map(s => <option key={s}>{s}</option>)}</select></label>
          <button type="button" className="bulk-update-button" onClick={applyBulk} disabled={bulkLoading}><span aria-hidden="true">✓</span>{bulkLoading ? 'Updating...' : 'Update status'}</button>
          <button type="button" className="bin-delete-button" disabled={bulkLoading} onClick={() => setPendingDelete({ ids: [...selected] })}><Icon name="delete" /><span>Move to bin</span></button>
          <button type="button" className="clear-selection" disabled={bulkLoading} onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      </div>}

      {actionError && <p className="api-message error">{actionError}</p>}
      {error && <p className="api-message error">{error}</p>}

      {loading ? <p className="api-message">Loading enquiries...</p> : (
        <div className="table-wrap"><table>
          <thead><tr>
            <th className="checkbox-cell"><input type="checkbox" className="row-check" checked={allChecked} onChange={toggleAll} aria-label="Select all" /></th>
            {headers.map(([label, field]) => (
              <th key={field} className="sortable" onClick={() => changeSort(field)}>{label}{sort.by === field && <span className="sort-arrow">{sort.dir === 'asc' ? '▲' : '▼'}</span>}</th>
            ))}
            <th>ACTIONS</th>
          </tr></thead>
          <tbody>{data.length === 0
            ? <tr><td colSpan={headers.length + 2} style={{ color: '#9a9daa' }}>No matching enquiries.</td></tr>
            : data.map(e => <tr key={e._id}>
              <td className="checkbox-cell"><input type="checkbox" className="row-check" checked={selected.has(e._id)} onChange={() => toggleRow(e._id)} aria-label={`Select ${fullName(e)}`} /></td>
              <td><strong>{fullName(e)}</strong></td>
              <td>{e.company_name || '-'}</td>
              <td>{e.mobile || '-'}</td>
              <td>{enquiryState(e, type)}</td>
              <td>{enquiryCity(e, type)}</td>
              <td><select value={e.status || 'New'} onChange={ev => rowStatusChange(e, ev.target.value)} style={{ ...selectStyle, ...STATUS_STYLE[e.status || 'New'] }}>{ENQUIRY_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}</select></td>
              <td>{formatDate(e.createdAt)}</td>
              <td><span className="job-row-actions">
                <button type="button" className="listing-action-button edit" onClick={() => openDetails(e)} aria-label="View details" title="View"><Icon name="file" /></button>
                <button type="button" className="listing-action-button delete" onClick={() => { setActionError(''); setPendingDelete({ ids: [e._id], name: fullName(e) }); }} aria-label="Delete" title="Delete"><Icon name="delete" /></button>
              </span></td>
            </tr>)}</tbody></table></div>
      )}

      {!loading && <ListingPagination page={page} pageSize={pageSize} total={pagination.total || 0} onPageChange={setPage} onPageSizeChange={size => { setPageSize(size); setPage(1); }} />}
    </section>

    {details && <div className="modal-backdrop" onMouseDown={e => { if (e.target === e.currentTarget) setDetails(null); }}>
      <div className="entry-modal" role="dialog" aria-modal="true" style={{ width: 'min(640px,100%)', maxHeight: '82vh', overflow: 'auto' }}>
        <h2>{fullName(details)}</h2>
        <p>{title.replace(/s$/, '')} details · received {formatDate(details.createdAt)}</p>
        {/* <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 14px' }}>
          <span className="field-label">Status</span>
          <select value={detailStatus} onChange={e => setDetailStatus(e.target.value)} style={selectStyle}>{ENQUIRY_STATUSES.map(s => <option key={s}>{s}</option>)}</select>
          <button type="button" className="primary-action" onClick={saveDetailStatus} disabled={detailStatus === details.status}>Save</button>
        </div> */}
        <div className="master-table-wrap"><table className="master-table"><tbody>
          {Object.entries(details).filter(([k]) => !['_id', '__v', 'status', 'createdAt', 'updatedAt', 'isDeleted', 'deletedAt'].includes(k)).map(([k, v]) => (
            <tr key={k}><td style={{ color: '#8d90a1', width: 210 }}>{fieldLabel(k)}</td><td><strong>{String(v || '-')}</strong></td></tr>
          ))}
        </tbody></table></div>
        <div className="confirm-actions"><button type="button" className="secondary-action" onClick={() => setDetails(null)}>Close</button></div>
      </div>
    </div>}

    {pendingDelete && <div className="modal-backdrop" onMouseDown={e => { if (e.target === e.currentTarget) setPendingDelete(null); }}>
      <div className="confirm-modal" role="dialog" aria-modal="true"><div className="confirm-icon">!</div><h2>Move {pendingDelete.ids.length > 1 ? 'Enquiries' : 'Enquiry'} to Bin?</h2>
        <p>{pendingDelete.ids.length === 1 ? <>Move the enquiry from <strong>"{pendingDelete.name || 'the selected record'}"</strong> to the bin?</> : <>Move <strong>{pendingDelete.ids.length} selected enquiries</strong> to the bin?</>} You can restore {pendingDelete.ids.length === 1 ? 'it' : 'them'} later.</p>
        {actionError && <div className="entry-error">{actionError}</div>}
        <div className="confirm-actions"><button type="button" className="secondary-action" disabled={bulkLoading} onClick={() => setPendingDelete(null)}>Cancel</button><button type="button" className="danger-action" disabled={bulkLoading} onClick={confirmDelete}>{bulkLoading ? 'Moving...' : 'Move to bin'}</button></div>
      </div>
    </div>}

    {showImport && <ImportModal type={type} title={title} onClose={() => setShowImport(false)} onDone={n => { setShowImport(false); setActionError(''); clearAll(); refresh(); window.alert(`${n} enquiries imported.`); }} />}
  </>;
}

/* --------------------------- Location master ---------------------------- */

const LOCATION_LEVELS = [
  { key: 'states', name: 'State', plural: 'States', icon: 'S', color: 'blue', description: 'Manage states available on the enquiry forms', parents: [] },
  { key: 'cities', name: 'City', plural: 'Cities', icon: 'C', color: 'orange', description: 'Manage cities, grouped by state', parents: ['state'] },
];

function LocationMaster({ locations, setLocations }) {
  const [selected, setSelected] = useState(null);
  const [entryModal, setEntryModal] = useState(null);
  const [form, setForm] = useState({ name: '', state: '', isActive: true });
  const [entryError, setEntryError] = useState('');
  const [busy, setBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const config = LOCATION_LEVELS.find(l => l.key === selected);
  const items = config ? locations[config.key] : [];
  const pagedItems = items.slice((page - 1) * pageSize, page * pageSize);
  const stateNameOptions = Array.from(new Set([...INDIA_STATE_NAMES, ...locations.states.map(s => s.name)])).sort((a, b) => a.localeCompare(b));

  useEffect(() => { setPage(1); }, [selected]);
  useEffect(() => { setPage(current => Math.min(current, Math.max(1, Math.ceil(items.length / pageSize)))); }, [items.length, pageSize]);

  const openModal = (mode, item = null) => {
    setEntryModal({ mode, item });
    const parentValues = {};
    config.parents.forEach(p => { parentValues[p] = item?.[p] || ''; });
    setForm({ name: item?.name || '', ...parentValues, isActive: item?.isActive ?? true });
    setEntryError('');
  };
  const closeModal = () => { setEntryModal(null); setEntryError(''); };

  const save = async e => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) { setEntryError(`${config.name} name is required`); return; }
    for (const parent of config.parents) if (!form[parent].trim()) { setEntryError(`${parent} is required`); return; }
    if (config.key === 'states' && INDIA_STATE_NAMES.some(s => s.toLowerCase() === name.toLowerCase())) {
      setEntryError(`"${name}" is already available by default — no need to add it`);
      return;
    }
    if (config.key === 'cities' && builtInCityNames(form.state).some(c => c.toLowerCase() === name.toLowerCase())) {
      setEntryError(`"${name}" is already available by default under ${form.state} — no need to add it`);
      return;
    }
    setBusy(true); setEntryError('');
    try {
      const payload = { name: form.name.trim(), isActive: form.isActive };
      config.parents.forEach(p => { payload[p] = form[p].trim(); });
      const saved = entryModal.mode === 'edit'
        ? await updateLocation(config.key, entryModal.item._id, payload)
        : await createLocation(config.key, payload);
      setLocations(current => ({
        ...current,
        [config.key]: entryModal.mode === 'edit'
          ? current[config.key].map(i => (i._id === saved._id ? saved : i))
          : [...current[config.key], saved].sort((a, b) => a.name.localeCompare(b.name)),
      }));
      closeModal();
    } catch (err) { setEntryError(err.message); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true); setEntryError('');
    try {
      await deleteLocation(config.key, pendingDelete._id);
      setLocations(current => ({ ...current, [config.key]: current[config.key].filter(i => i._id !== pendingDelete._id) }));
      setPendingDelete(null);
    } catch (err) { setEntryError(err.message); }
    finally { setBusy(false); }
  };

  if (!config) return <>
    <PageHeader eyebrow="MASTERS" name="Location Master" description="Add any state or city missing from the enquiry-form dropdowns." />
    <div className="master-category-grid">
      {LOCATION_LEVELS.map(l => <MasterCard key={l.key} title={l.plural} description={l.description} count={locations[l.key].length} icon={l.icon} color={l.color} onClick={() => setSelected(l.key)} />)}
    </div>
  </>;

  return <>
    <div className="master-detail-header"><button onClick={() => setSelected(null)}><Icon name="arrowLeft" /> Back to Location Master</button></div>
    <PageHeader eyebrow="MASTER MANAGEMENT" name={config.plural} description={`Add, view, and remove ${config.plural.toLowerCase()}.`} />
    <section className="panel master-detail-panel">
      <div className="master-detail-title"><span className={`master-category-icon ${config.color}`}>{config.icon}</span><div><h2>{config.plural}</h2><p>{items.length} entries available</p></div><button className="primary-action master-add-button" onClick={() => openModal('add')}><Icon name="plus" /> Add {config.name}</button></div>
      <div className="master-table-wrap"><table className="master-table"><thead><tr><th>#</th>{config.parents.map(p => <th key={p}>{p.toUpperCase()}</th>)}<th>{config.name.toUpperCase()} NAME</th><th>STATUS</th><th>CREATED</th><th>ACTION</th></tr></thead>
        <tbody>{items.length === 0
          ? <tr><td colSpan={5 + config.parents.length} style={{ color: '#9a9daa' }}>No {config.plural.toLowerCase()} added yet.</td></tr>
          : pagedItems.map((item, index) => <tr key={item._id}>
            <td>{String((page - 1) * pageSize + index + 1).padStart(2, '0')}</td>
            {config.parents.map(p => <td key={p}>{item[p]}</td>)}
            <td><span className="master-table-name"><i>{item.name[0]}</i><strong>{item.name}</strong></span></td>
            <td><span className={`master-active-status${item.isActive ? '' : ' inactive'}`}><i className="status-dot" /> {item.isActive ? 'Active' : 'Inactive'}</span></td>
            <td>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '-'}</td>
            <td><span className="master-row-actions">
              <button className="listing-action-button edit" type="button" onClick={() => openModal('edit', item)} aria-label={`Edit ${item.name}`} title="Edit"><Icon name="edit" /></button>
              <button className="listing-action-button delete" type="button" onClick={() => setPendingDelete(item)} aria-label={`Delete ${item.name}`} title="Delete"><Icon name="delete" /></button>
            </span></td>
          </tr>)}</tbody></table></div>
      <ListingPagination page={page} pageSize={pageSize} total={items.length} onPageChange={setPage} onPageSizeChange={size => { setPageSize(size); setPage(1); }} />
    </section>

    {entryModal && <div className="modal-backdrop" onMouseDown={e => { if (e.target === e.currentTarget) closeModal(); }}>
      <form className="entry-modal" role="dialog" aria-modal="true" onSubmit={save}>
        <div className={`master-category-icon ${config.color}`}>{config.icon}</div>
        <h2>{entryModal.mode === 'edit' ? 'Edit' : 'Add'} {config.name}</h2>
        <p>{entryModal.mode === 'edit' ? `Update the selected ${config.name.toLowerCase()}.` : `Create a new ${config.name.toLowerCase()} for the enquiry forms.`}</p>
       
        {config.parents.includes('state') && <label>State
          <select value={form.state} onChange={e => { setForm({ ...form, state: e.target.value }); setEntryError(''); }}>
            <option value="">Select a state</option>
            {stateNameOptions.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>}
        <label>{config.name} name<input autoFocus value={form.name} onChange={e => { setForm({ ...form, name: e.target.value }); setEntryError(''); }} placeholder={`Enter ${config.name.toLowerCase()} name`} /></label>
        <label>Status<select value={form.isActive ? 'active' : 'inactive'} onChange={e => setForm({ ...form, isActive: e.target.value === 'active' })}><option value="active">Active</option><option value="inactive">Inactive</option></select></label>
        {entryError && <div className="entry-error">{entryError}</div>}
        <div className="confirm-actions"><button type="button" className="secondary-action" onClick={closeModal}>Cancel</button><button disabled={busy} className="primary-action">{busy ? 'Saving...' : entryModal.mode === 'edit' ? 'Save changes' : `Add ${config.name}`}</button></div>
      </form>
    </div>}

    {pendingDelete && <div className="modal-backdrop" onMouseDown={e => { if (e.target === e.currentTarget) setPendingDelete(null); }}>
      <div className="confirm-modal" role="dialog" aria-modal="true"><div className="confirm-icon">!</div><h2>Delete {config.name}?</h2>
        <p>Delete <strong>"{pendingDelete.name}"</strong>? It will no longer appear in the enquiry-form dropdowns.</p>
        {entryError && <div className="entry-error">{entryError}</div>}
        <div className="confirm-actions"><button type="button" className="secondary-action" onClick={() => setPendingDelete(null)}>Cancel</button><button disabled={busy} type="button" className="danger-action" onClick={remove}>{busy ? 'Deleting...' : 'Delete entry'}</button></div>
      </div>
    </div>}
  </>;
}

function LocationMasterSection() {
  const [locations, setLocations] = useState(null);
  useEffect(() => { fetchLocations().then(setLocations).catch(() => setLocations({ states: [], cities: [] })); }, []);
  if (!locations) return <p className="api-message">Loading location master...</p>;
  return <LocationMaster locations={locations} setLocations={setLocations} />;
}

/* ------------------------------ Mail IDs ------------------------------- */

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const selectedValues = event => Array.from(event.target.selectedOptions, option => option.value);

function MailIdsSection() {
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState({ states: [], cities: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ emails: '', states: [], cities: [] });
  const [busy, setBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingResend, setPendingResend] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState({ email: '', state: '', city: '', dateFrom: '', dateTo: '' });

  useEffect(() => {
    Promise.all([fetchMailIds(), fetchLocations()])
      .then(([mailIds, locationData]) => { setItems(mailIds); setLocations(locationData); setError(''); })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const stateOptions = Array.from(new Set([...INDIA_STATE_NAMES, ...locations.states.map(item => item.name)])).sort((a, b) => a.localeCompare(b));
  const cityOptions = Array.from(new Set(form.states.flatMap(state => [
    ...builtInCityNames(state),
    ...locations.cities.filter(city => city.state === state).map(city => city.name),
  ]))).sort((a, b) => a.localeCompare(b));
  const filterStateOptions = Array.from(new Set(items.flatMap(item => item.states || []))).sort((a, b) => a.localeCompare(b));
  const filterCityOptions = Array.from(new Set(items
    .filter(item => !filters.state || item.states?.includes(filters.state))
    .flatMap(item => item.cities || []))).sort((a, b) => a.localeCompare(b));
  const filteredItems = items.filter(item => {
    const emailMatch = !filters.email || item.emails?.some(email => email.toLowerCase().includes(filters.email.trim().toLowerCase()));
    const stateMatch = !filters.state || item.states?.includes(filters.state);
    const cityMatch = !filters.city || item.cities?.includes(filters.city);
    const created = item.createdAt ? new Date(item.createdAt) : null;
    const fromMatch = !filters.dateFrom || (created && created >= new Date(`${filters.dateFrom}T00:00:00`));
    const toMatch = !filters.dateTo || (created && created <= new Date(`${filters.dateTo}T23:59:59.999`));
    return emailMatch && stateMatch && cityMatch && fromMatch && toMatch;
  });
  const pagedItems = filteredItems.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => { setPage(current => Math.min(current, Math.max(1, Math.ceil(filteredItems.length / pageSize)))); }, [filteredItems.length, pageSize]);

  const setFilter = (name, value) => {
    setFilters(current => ({ ...current, [name]: value, ...(name === 'state' ? { city: '' } : {}) }));
    setPage(1);
  };
  const resetFilters = () => {
    setFilters({ email: '', state: '', city: '', dateFrom: '', dateTo: '' });
    setPage(1);
  };

  const openModal = (item = null) => {
    setModal(item ? { mode: 'edit', item } : { mode: 'add' });
    setForm({ emails: item?.emails?.join(', ') || '', states: item?.states || [], cities: item?.cities || [] });
    setError('');
  };

  const save = async event => {
    event.preventDefault();
    const emails = [...new Set(form.emails.split(',').map(value => value.trim().toLowerCase()).filter(Boolean))];
    if (!emails.length) { setError('Enter at least one mail ID'); return; }
    const invalid = emails.find(email => !EMAIL_PATTERN.test(email));
    if (invalid) { setError(`Invalid mail ID: ${invalid}`); return; }
    setBusy(true); setError('');
    try {
      const payload = { emails, states: form.states, cities: form.cities };
      const saved = modal.mode === 'edit' ? await updateMailId(modal.item._id, payload) : await createMailId(payload);
      setItems(current => modal.mode === 'edit' ? current.map(item => item._id === saved._id ? saved : item) : [saved, ...current]);
      setModal(null);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true); setError('');
    try {
      await deleteMailId(pendingDelete._id);
      setItems(current => current.filter(item => item._id !== pendingDelete._id));
      setPendingDelete(null);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const resendNotifications = async () => {
    setBusy(true); setError(''); setSuccessMessage('');
    try {
      const result = await resendPendingNotifications(pendingResend._id);
      setSuccessMessage(result.message);
      setPendingResend(null);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  return <>
    <PageHeader eyebrow="CHANNEL PARTNER" name="Map Email IDs" description="Map notification email IDs to a state, city, or all Channel Partner locations from one place." action={<button className="primary-action" onClick={() => openModal()}><Icon name="plus" /> Map Email ID</button>} />
    {error && !modal && !pendingDelete && !pendingResend && <p className="api-message error">{error}</p>}
    {successMessage && <p className="api-message">{successMessage}</p>}
    <section className="panel master-detail-panel">
      <div className="master-detail-title"><span className="master-category-icon blue">@</span><div><h2>Location Email Mapping</h2><p>{filteredItems.length === items.length ? `${items.length} ${items.length === 1 ? 'mapping' : 'mappings'} available` : `${filteredItems.length} of ${items.length} mappings`}</p></div></div>
      <div className="mail-id-filters">
        <div className="filter-bar mail-id-filter-primary">
          <input className="filter-search" type="search" placeholder="Filter by mail ID..." value={filters.email} onChange={event => setFilter('email', event.target.value)} />
          <select value={filters.state} onChange={event => setFilter('state', event.target.value)}><option value="">All states</option>{filterStateOptions.map(state => <option key={state} value={state}>{state}</option>)}</select>
          <select value={filters.city} onChange={event => setFilter('city', event.target.value)}><option value="">All cities</option>{filterCityOptions.map(city => <option key={city} value={city}>{city}</option>)}</select>
        </div>
        <div className="filter-bar mail-id-filter-dates">
          <label>From<input type="date" value={filters.dateFrom} max={filters.dateTo || undefined} onChange={event => setFilter('dateFrom', event.target.value)} /></label>
          <label>To<input type="date" value={filters.dateTo} min={filters.dateFrom || undefined} onChange={event => setFilter('dateTo', event.target.value)} /></label>
          <button type="button" className="reset-filters" onClick={resetFilters}><span aria-hidden="true">↻</span> Reset</button>
        </div>
      </div>
      {loading ? <p className="api-message">Loading mail IDs...</p> : <div className="master-table-wrap"><table className="master-table mail-id-table"><thead><tr><th>#</th><th>MAIL IDS</th><th>STATES</th><th>CITIES</th><th>CREATED</th><th>ACTION</th></tr></thead><tbody>
        {!filteredItems.length ? <tr><td colSpan="6" className="empty-table-cell">{items.length ? 'No mail IDs match the selected filters.' : 'No mail IDs added yet.'}</td></tr> : pagedItems.map((item, index) => <tr key={item._id}>
          <td>{String((page - 1) * pageSize + index + 1).padStart(2, '0')}</td>
          <td><strong>{item.emails.join(', ')}</strong></td>
          <td>{item.states?.length ? item.states.join(', ') : 'All locations'}</td>
          <td>{item.cities?.length ? item.cities.join(', ') : 'All cities'}</td>
          <td>{formatDate(item.createdAt)}</td>
          <td><span className="master-row-actions"><button className="listing-action-button edit" type="button" onClick={() => { setPendingResend(item); setError(''); setSuccessMessage(''); }} title="Send pending request notifications" aria-label={`Send pending request notifications to ${item.emails.join(', ')}`}><Icon name="mail" /></button><button className="listing-action-button edit" type="button" onClick={() => openModal(item)} title="Edit"><Icon name="edit" /></button><button className="listing-action-button delete" type="button" onClick={() => { setPendingDelete(item); setError(''); }} title="Delete"><Icon name="delete" /></button></span></td>
        </tr>)}</tbody></table></div>}
      {!loading && <ListingPagination page={page} pageSize={pageSize} total={filteredItems.length} onPageChange={setPage} onPageSizeChange={size => { setPageSize(size); setPage(1); }} />}
    </section>

    {modal && <div className="modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget && !busy) setModal(null); }}><form className="entry-modal mail-id-modal" role="dialog" aria-modal="true" onSubmit={save}>
      <div className="master-category-icon blue">@</div><h2>{modal.mode === 'edit' ? 'Edit' : 'Map'} Email IDs</h2><p>Enter one or more email addresses separated by commas. Leave state and city empty to create the default mapping for all Channel Partner locations.</p>
      <label>Mail IDs <textarea autoFocus value={form.emails} onChange={event => { setForm({ ...form, emails: event.target.value }); setError(''); }} placeholder="sales@example.com, support@example.com" rows="3" /></label>
      <label>Locations — States <select multiple size="5" value={form.states} onChange={event => { const states = selectedValues(event); setForm(current => ({ ...current, states, cities: current.cities.filter(city => states.some(state => builtInCityNames(state).includes(city) || locations.cities.some(item => item.state === state && item.name === city))) })); }}>
        {stateOptions.map(state => <option key={state} value={state}>{state}</option>)}
      </select><small>Hold Ctrl (Windows) or Command (Mac) to select multiple.</small></label>
      <label>Locations — Cities <select multiple size="5" value={form.cities} disabled={!form.states.length} onChange={event => setForm({ ...form, cities: selectedValues(event) })}>
        {cityOptions.map(city => <option key={city} value={city}>{city}</option>)}
      </select><small>{form.states.length ? 'Choose any cities covered by this entry.' : 'Select one or more states to choose cities.'}</small></label>
      {error && <div className="entry-error">{error}</div>}
      <div className="confirm-actions"><button type="button" className="secondary-action" disabled={busy} onClick={() => setModal(null)}>Cancel</button><button className="primary-action" disabled={busy}>{busy ? 'Saving...' : modal.mode === 'edit' ? 'Save changes' : 'Save mapping'}</button></div>
    </form></div>}

    {pendingDelete && <div className="modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget && !busy) setPendingDelete(null); }}><div className="confirm-modal" role="dialog" aria-modal="true"><div className="confirm-icon">!</div><h2>Delete Mail IDs?</h2><p>Delete <strong>{pendingDelete.emails.join(', ')}</strong>? This action cannot be undone.</p>{error && <div className="entry-error">{error}</div>}<div className="confirm-actions"><button type="button" className="secondary-action" disabled={busy} onClick={() => setPendingDelete(null)}>Cancel</button><button type="button" className="danger-action" disabled={busy} onClick={remove}>{busy ? 'Deleting...' : 'Delete entry'}</button></div></div></div>}
    {pendingResend && <div className="modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget && !busy) setPendingResend(null); }}><div className="confirm-modal" role="dialog" aria-modal="true"><div className="confirm-icon">@</div><h2>Send Pending Notifications?</h2><p>Send each matching <strong>New</strong> or <strong>In Progress</strong> distributor and super-stockist enquiry to <strong>{pendingResend.emails.join(', ')}</strong>.</p>{error && <div className="entry-error">{error}</div>}<div className="confirm-actions"><button type="button" className="secondary-action" disabled={busy} onClick={() => setPendingResend(null)}>Cancel</button><button type="button" className="primary-action" disabled={busy} onClick={resendNotifications}>{busy ? 'Sending...' : 'Send notifications'}</button></div></div></div>}
  </>;
}

function BinListing({ type, items, loading, error, onRestore, onPermanentDelete }) {
  const isApplication = type === 'distributor-applications';
  const singular = isApplication ? 'distributor-applications' : 'super-stockist-applications';
  const [selected, setSelected] = useState(new Set());
  const [pending, setPending] = useState(null);
  const [details, setDetails] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const pagedItems = items.slice((page - 1) * pageSize, page * pageSize);
  const allChecked = pagedItems.length > 0 && pagedItems.every(item => selected.has(item._id));
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(pagedItems.map(item => item._id)));
  const toggleRow = id => setSelected(current => { const next = new Set(current); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const requestAction = (action, ids) => { setActionError(''); setPending({ action, ids }); };
  useEffect(() => { setPage(current => Math.min(current, Math.max(1, Math.ceil(items.length / pageSize)))); }, [items.length, pageSize]);
  const runAction = async () => {
    setActionLoading(true); setActionError('');
    try {
      if (pending.action === 'restore') await onRestore(pending.ids);
      else await onPermanentDelete(pending.ids);
      setSelected(current => { const next = new Set(current); pending.ids.forEach(id => next.delete(id)); return next; });
      setPending(null);
    } catch (actionErr) { setActionError(actionErr.message); }
    finally { setActionLoading(false); }
  };
  return <>
    <PageHeader eyebrow="ARCHIVE" name={isApplication ? 'Distributor Bin' : 'Super Stockist Bin'} description={`${items.length} deleted ${singular}${items.length === 1 ? '' : 's'} retained in the database.`} />
    {error && <p className="api-message error">{error}</p>}
    {loading ? <p className="api-message">Loading deleted {singular}s...</p> : <div className="bin-grid"><section className="panel jobs-panel bin-panel">
      <div className="panel-title"><div><h2>{isApplication ? 'Distributor Enquiries' : 'Super Stockist Enquiries'}</h2><p>{items.length} deleted {singular}{items.length === 1 ? '' : 's'}</p></div></div>
      {selected.size > 0 && <div className="bulk-bar bin-bulk-bar">
        <div className="bulk-selection-count"><span>{selected.size}</span><div><strong>{selected.size === 1 ? 'Enquiry selected' : 'Enquiries selected'}</strong><small>Choose what to do with the selected records</small></div></div>
        <div className="bulk-actions">
          <button type="button" className="bin-restore-button" disabled={actionLoading} onClick={() => requestAction('restore', [...selected])}><span className="bin-action-icon">↺</span><span>Restore</span></button>
          <button type="button" className="bin-delete-button" disabled={actionLoading} onClick={() => requestAction('delete', [...selected])}><Icon name="delete" /><span>Delete permanently</span></button>
          <button type="button" className="clear-selection" disabled={actionLoading} onClick={() => setSelected(new Set())}>Clear selection</button>
        </div>
      </div>}
      <div className="table-wrap"><table><thead><tr><th className="checkbox-cell"><input type="checkbox" className="row-check" checked={allChecked} onChange={toggleAll} aria-label={`Select all deleted ${singular}s`} /></th>{isApplication ? <><th>NAME</th><th>COMPANY</th><th>LOCATION</th></> : <><th>FULL NAME</th><th>COMPANY</th><th>EMAIL</th></>}<th>STATUS</th><th>MOVED TO BIN</th><th>ACTIONS</th></tr></thead><tbody>
        {pagedItems.map(item => <tr key={item._id}><td className="checkbox-cell"><input type="checkbox" className="row-check" checked={selected.has(item._id)} onChange={() => toggleRow(item._id)} aria-label={`Select ${isApplication ? item.title || item.position : item.fullName}`} /></td>{isApplication ? <><td><strong>{item.title || item.position}</strong></td><td>{item.department || '-'}</td><td>{item.city || 'Remote'}</td></> : <><td><strong>{item.fullName}</strong></td><td>{item.jobTitle || '-'}</td><td>{item.email || '-'}</td></>}<td><span className={`status ${(item.status || (isApplication ? 'Draft' : 'Applied')).toLowerCase()}`}><i className="status-dot" /> {item.status || (isApplication ? 'Draft' : 'Applied')}</span></td><td>{item.updatedAt ? new Date(item.updatedAt).toLocaleString() : '-'}</td><td><span className="job-row-actions"><button type="button" className="listing-action-button edit" onClick={() => setDetails(item)} title="View details" aria-label={`View ${singular} details`}><Icon name="file" /></button><button type="button" className="listing-action-button edit" disabled={actionLoading} onClick={() => requestAction('restore', [item._id])} title="Restore" aria-label={`Restore ${singular}`}>↺</button><button type="button" className="listing-action-button delete" disabled={actionLoading} onClick={() => requestAction('delete', [item._id])} title="Delete permanently" aria-label={`Delete ${singular} permanently`}><Icon name="delete" /></button></span></td></tr>)}
        {!items.length && <tr><td colSpan="7" className="empty-table-cell">No deleted {singular}s.</td></tr>}
      </tbody></table></div>
      <ListingPagination page={page} pageSize={pageSize} total={items.length} onPageChange={setPage} onPageSizeChange={size => { setPageSize(size); setPage(1); }} />
    </section></div>}
    {details && <div className="modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) setDetails(null); }}>
      <div className="entry-modal" role="dialog" aria-modal="true" style={{ width: 'min(720px,100%)', maxHeight: '82vh', overflow: 'auto' }}>
        <div className="master-category-icon blue"><Icon name="file" /></div>
        <h2>{details.title || details.fullName || 'Enquiry Details'}</h2>
        <p>Complete archived {isApplication ? 'distributor' : 'super stockist'} enquiry record.</p>
        <div className="master-table-wrap"><table className="master-table"><tbody>
          {Object.entries(details).filter(([key]) => !['__v', 'title', 'department', 'city', 'fullName', 'jobTitle'].includes(key)).map(([key, value]) => (
            <tr key={key}><td style={{ color: '#8d90a1', width: 220 }}>{fieldLabel(key)}</td><td><strong>{value === null || value === undefined || value === '' ? '-' : typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}</strong></td></tr>
          ))}
        </tbody></table></div>
        <div className="confirm-actions"><button type="button" className="secondary-action" onClick={() => setDetails(null)}>Close</button></div>
      </div>
    </div>}
    {pending && <div className="modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget && !actionLoading) setPending(null); }}><div className="confirm-modal" role="dialog" aria-modal="true"><div className="confirm-icon">!</div><h2>{pending.action === 'restore' ? 'Restore' : 'Permanently Delete'} {pending.ids.length > 1 ? `Selected ${isApplication ? 'Job Openings' : 'Applications'}` : isApplication ? 'Job Opening' : 'Application'}?</h2><p>{pending.action === 'restore' ? `${pending.ids.length} ${singular}${pending.ids.length === 1 ? '' : 's'} will be returned to the active listing.` : `${pending.ids.length} ${singular}${pending.ids.length === 1 ? '' : 's'} will be permanently deleted. This action cannot be undone.`}</p>{actionError && <div className="entry-error">{actionError}</div>}<div className="confirm-actions"><button type="button" className="secondary-action" disabled={actionLoading} onClick={() => setPending(null)}>Cancel</button><button type="button" className={pending.action === 'restore' ? 'primary-action' : 'danger-action'} disabled={actionLoading} onClick={runAction}>{actionLoading ? 'Processing...' : pending.action === 'restore' ? 'Restore' : 'Delete permanently'}</button></div></div></div>}
  </>;
}

function BinSection({ type }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { setCounts } = useEnquiryCounts();
  const countKey = type === 'distributors' ? 'distributorBin' : 'superStockistBin';

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchBin(type);
      const displayItems = (data || []).map(item => ({
        ...item,
        title: fullName(item),
        department: item.company_name || '-',
        city: `${type === 'distributors' ? item.agency_city || '-' : item.current_city || '-'}, ${type === 'distributors' ? item.agency_state || '-' : item.current_state || '-'}`,
        fullName: fullName(item),
        jobTitle: item.company_name || '-',
        updatedAt: item.deletedAt,
      }));
      setItems(displayItems);
      setCounts(current => ({ ...current, [countKey]: data?.length || 0 }));
      setError('');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (action, ids) => {
    if (action === 'restore') {
      await restoreFromBin(type, ids);
      const activeKey = type === 'distributors' ? 'distributor' : 'superStockist';
      setCounts(current => ({ ...current, [activeKey]: (current[activeKey] || 0) + ids.length }));
    }
    else await permanentlyDeleteFromBin(type, ids);
    await load();
  };

  const binType = type === 'distributors' ? 'distributor-applications' : 'super-stockist-applications';
  return <BinListing type={binType} items={items} loading={loading} error={error} onRestore={ids => run('restore', ids)} onPermanentDelete={ids => run('delete', ids)} />;
}

function Placeholder({ section }) {
  return <><PageHeader eyebrow="CHANNEL PARTNER" name={sectionNames[section]} description={`Manage your ${sectionNames[section].toLowerCase()} from one place.`} /><section className="panel empty-panel"><div><Icon name="diamond" /></div><h2>{sectionNames[section]}</h2><p>This workspace is ready for your channel partner content.</p><button className="primary-action">Get started</button></section></>;
}
/* ------------------------------- Root page ------------------------------ */

function ChannelPartnerAdmin({ activeSection = 'overview' }) {
  if (activeSection === 'overview') return <Overview />;
  if (activeSection === 'distributors') return <EnquirySection type="distributors" title="Distributor Enquiries" />;
  if (activeSection === 'super-stockists') return <EnquirySection type="super-stockists" title="Super Stockist Enquiries" />;
  if (activeSection === 'location-master') return <LocationMasterSection />;
  if (activeSection === 'mail-ids') return <MailIdsSection />;
  if (activeSection === 'distributor-bin') return <BinSection type="distributors" />;
  if (activeSection === 'super-stockist-bin') return <BinSection type="super-stockists" />;

  return <Placeholder section={activeSection} />;

}

export default ChannelPartnerAdmin;
